import Component from '../core/Component'
import EventBus from '../core/EventBus'
import support from '../utils/BrowserSupport'
import { toggleClass } from '../utils/dom'
import Motion from '../utils/Motion'
import { CursorTriggerEvents } from '../meta/CursorTrigger'
import { EVENTS } from '../core/Events'
import throttle from 'lodash/throttle'

const defaults = {
    symbol: 'default',
    variant: 'dark',
    size: 'medium',
    alwaysVisible: false,
}

const ACCELERATION = 0.1
const FRICTION = 0.0075

export default class Cursor extends Component {
    constructor(element) {
        super(element, defaults)

        this.ref = {
            content: null,
            spread: null,
        }

        const state = {
            clicked: false,
            active: false,
            visible: false,
            symbol: defaults.symbol,
            variant: defaults.variant,
            size: defaults.size,
        }

        this.hasMoved = false

        this.state = new Proxy(state, {
            set: (obj, prop, value) => {
                if (obj[prop] !== value) {
                    if (prop === 'symbol' || prop === 'variant' || prop === 'size') {
                        this.element.classList.remove(`is-${obj[prop]}`)
                        this.element.classList.add(`is-${value}`)
                        obj[prop] = value
                    } else {
                        obj[prop] = value
                        toggleClass(this.element, `is-${prop}`, value)
                    }
                }
                return true
            },
        })

        this.x = 0
        this.y = 0
    }

    prepare() {
        this.element.addEventListener(support.transitionEnd, this.handleTransitionEnd)
        window.addEventListener('mousemove', this.handleMouseMove)
        document.addEventListener('click', this.handleClick)
        EventBus.on(CursorTriggerEvents.RegionEnter, this.handleCursorRegionEnter)
        EventBus.on(CursorTriggerEvents.RegionLeave, this.handleCursorRegionLeave)
        EventBus.on(EVENTS.SWUP_WILL_REPLACE_CONTENT, this.handleCursorRegionLeave)

        this.motionX = new Motion({
            acceleration: ACCELERATION,
            friction: FRICTION,
            onProgress: this.handleMotionXProgress
        })
        this.motionY = new Motion({
            acceleration: ACCELERATION,
            friction: FRICTION,
            onProgress: this.handleMotionYProgress
        })

        if (this.options.alwaysVisible) {
            this.state.active = true
            this.state.visible = true
        }
    }

    destroy() {
        window.removeEventListener('mousemove', this.handleMouseMove)
        document.removeEventListener('click', this.handleClick)
        EventBus.off(CursorTriggerEvents.RegionEnter, this.handleCursorRegionEnter)
        EventBus.off(CursorTriggerEvents.RegionLeave, this.handleCursorRegionLeave)
        EventBus.off(EVENTS.SWUP_WILL_REPLACE_CONTENT, this.handleCursorRegionLeave)
    }

    handleTransitionEnd = (event) => {
        if (event.target === this.element && !this.state.active) {
            this.state.visible = false
        }

        if (event.target === this.ref.spread && this.state.clicked) {
            this.state.clicked = false
        }
    }

    handleMouseMove = (event) => {
        if (!this.hasMoved) {
            this.hasMoved = true

            this.motionX.set({
                current: event.clientX
            })
            this.motionY.set({
                current: event.clientY,
            })
        }

        if (!this.state.visible) {
            this.motionX.set({
                current: event.clientX,
            })
            this.motionY.set({
                current: event.clientY,
            })
            return
        }

        this.motionX.set({
            to: event.clientX,
        })
        this.motionY.set({
            to: event.clientY,
        })
        this.motionX.animate()
        this.motionY.animate()
    }

    handleCursorRegionEnter = ({
        symbol = defaults.symbol,
        variant = defaults.variant,
        size = defaults.size,
    }) => {
        if (!this.hasMoved) {
            return
        }

        let changed = false

        if (symbol !== this.state.symbol) {
            this.state.symbol = symbol
            changed = true
        }

        if (variant !== this.state.variant) {
            this.state.variant = variant
            changed = true
        }

        if (size !== this.state.size) {
            this.state.size = size
            changed = true
        }

        if (changed) {
            this.element.offsetWidth
        }

        if (!this.state.active) {
            this.state.active = true
            this.state.visible = true
        }
    }

    handleCursorRegionLeave = () => {
        if (!this.hasMoved) {
            return
        }

        if (!this.options.alwaysVisible) {
            this.state.active = false
        }

        this.state.variant = defaults.variant
        this.state.symbol = defaults.symbol
        this.state.size = defaults.size
    }

    handleClick = () => {
        if (this.state.visible && !this.state.clicked) {
            this.state.clicked = true
        }
    }

    handleMotionXProgress = (progress) => {
        this.x = progress
        this.render()
    }

    handleMotionYProgress = (progress) => {
        this.y = progress
        this.render()
    }

    render = throttle(() => {
        this.element.style.transform = `translate3d(${this.x}px, ${this.y}px, 0)`
    }, 15)
}
